import { ReactNode, useId, useState } from 'react'

/** Borderless icon button used in the drawer header. */
const IconGhostButton = ({
  icon,
  title,
  onClick,
  disabled,
  ariaDisabled,
  description,
  active,
  ariaLabel,
}: {
  icon: ReactNode
  title?: string
  /** The button's name when the title says something else (e.g. why it is disabled). */
  ariaLabel?: string
  onClick?: () => void
  disabled?: boolean
  /**
   * Unavailable for now, but still reachable with the keyboard and read out with its
   * `description` (why it is unavailable): a click does nothing. Looks like `disabled`.
   */
  ariaDisabled?: boolean
  /** Read out after the button's name (e.g. why it cannot be used yet). */
  description?: string
  /** The panel this button toggles is open. */
  active?: boolean
}) => {
  const [hover, setHover] = useState(false)
  const descriptionId = useId()
  const inactive = !!disabled || !!ariaDisabled
  return (
    <>
      <button
        type="button"
        title={title}
        aria-label={ariaLabel}
        disabled={disabled}
        aria-disabled={ariaDisabled || undefined}
        aria-describedby={description ? descriptionId : undefined}
        aria-pressed={active}
        onClick={ariaDisabled ? undefined : onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={`flex rounded-lg border-0 p-[6px] text-[17px] ${
          ariaDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
        style={{
          color: active ? '#008aa3' : '#5a6474',
          background: active
            ? '#e7f7fb'
            : hover && !inactive
            ? '#f1f3f8'
            : 'transparent',
          opacity: inactive ? 0.4 : 1,
        }}
      >
        {icon}
      </button>
      {/* Outside the button: inside, it would become the name of a button without ariaLabel. */}
      {description && (
        <span id={descriptionId} className="sr-only">
          {description}
        </span>
      )}
    </>
  )
}

export default IconGhostButton
